import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { API_URL } from '../config';

function normalizeDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function normalizeIco(value) {
  const digits = normalizeDigits(value);
  return digits;
}

function getPhoneVariants(value) {
  let digits = normalizeDigits(value);
  if (!digits) return [];

  if (digits.startsWith('00') && digits.length > 2) {
    digits = digits.slice(2);
  }

  const variants = new Set();

  if (digits.length === 9) {
    variants.add(digits);
    variants.add(`420${digits}`);
    return Array.from(variants);
  }

  if (digits.startsWith('420') && digits.length >= 12) {
    const local = digits.slice(-9);
    variants.add(local);
    variants.add(`420${local}`);
    variants.add(digits);
    return Array.from(variants);
  }

  if (digits.length > 9) {
    const local = digits.slice(-9);
    variants.add(local);
    variants.add(`420${local}`);
    variants.add(digits);
    return Array.from(variants);
  }

  return [];
}

export default function MsisdnIcoFixModal({ onClose, isDarkMode, currentUserId, onUpdated, companies = [] }) {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [previewRows, setPreviewRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [result, setResult] = useState(null);

  const changedRows = useMemo(
    () => previewRows.filter((r) => r.status === 'changed'),
    [previewRows]
  );

  const resetState = () => {
    setPreviewRows([]);
    setStats(null);
    setResult(null);
    setMessage('');
    setProgress(0);
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    resetState();
  };

  const buildPreview = async () => {
    if (!file) {
      alert('Vyberte prosím Excel soubor (.xlsx).');
      return;
    }

    setIsProcessing(true);
    setProgress(5);
    setMessage('Načítám Excel...');

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (!rows || rows.length === 0) {
        throw new Error('Excel je prázdný.');
      }

      const parsed = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i] || [];
        const phoneRaw = row[0];
        const icoRaw = row[1];
        const variants = getPhoneVariants(phoneRaw);
        const newIco = normalizeIco(icoRaw);

        const hasAnyData = String(phoneRaw || '').trim() || String(icoRaw || '').trim();
        if (!hasAnyData) continue;

        if (variants.length === 0 || !newIco) {
          parsed.push({
            rowNumber: i + 1,
            phoneInput: String(phoneRaw || ''),
            newIcoInput: String(icoRaw || ''),
            variants,
            newIco,
            invalid: true,
          });
          continue;
        }

        parsed.push({
          rowNumber: i + 1,
          phoneInput: String(phoneRaw || ''),
          newIcoInput: String(icoRaw || ''),
          variants,
          newIco,
          invalid: false,
        });
      }

      const validEntries = parsed.filter((p) => !p.invalid);
      if (validEntries.length === 0) {
        throw new Error('V Excelu nebyly nalezeny validní řádky (sloupec A: telefon, sloupec B: IČO).');
      }

      if (!companies || companies.length === 0) {
        throw new Error('Nejsou načtené žádné firmy ve Správě kontaktů. Nejprve si načtěte firmy, nad kterými chcete opravu spustit.');
      }

      setProgress(20);
      setMessage(`Indexuji telefony v přednačtených firmách (${companies.length})...`);

      const companyMap = new Map();
      companies.forEach((company) => {
        if (company?._id) companyMap.set(String(company._id), company);
      });

      setProgress(45);
      setMessage('Připravuji seznam změn...');

      const variantToCompanyIds = new Map();
      companies.forEach((company) => {
        (company.contacts || []).forEach((contact) => {
          const phoneVariants = getPhoneVariants(contact?.phone);
          phoneVariants.forEach((v) => {
            if (!variantToCompanyIds.has(v)) variantToCompanyIds.set(v, new Set());
            variantToCompanyIds.get(v).add(String(company._id));
          });
        });
      });

      const finalRows = parsed.map((entry) => {
        if (entry.invalid) {
          return {
            ...entry,
            status: 'invalid',
            statusLabel: 'Neplatný řádek',
            reason: 'Telefon nebo IČO není validní',
            companyName: '',
            companyId: '',
            oldIco: '',
          };
        }

        const matchedCompanyIds = new Set();
        entry.variants.forEach((variant) => {
          const ids = variantToCompanyIds.get(variant);
          if (ids) ids.forEach((id) => matchedCompanyIds.add(id));
        });

        if (matchedCompanyIds.size === 0) {
          return {
            ...entry,
            status: 'not-found',
            statusLabel: 'Nenalezeno',
            reason: 'K telefonu nebyla nalezena firma',
            companyName: '',
            companyId: '',
            oldIco: '',
          };
        }

        if (matchedCompanyIds.size > 1) {
          const names = Array.from(matchedCompanyIds)
            .map((id) => companyMap.get(id)?.name)
            .filter(Boolean)
            .slice(0, 3)
            .join(', ');
          return {
            ...entry,
            status: 'ambiguous',
            statusLabel: 'Více firem',
            reason: names ? `Telefon patří více firmám: ${names}` : 'Telefon patří více firmám',
            companyName: '',
            companyId: '',
            oldIco: '',
          };
        }

        const companyId = Array.from(matchedCompanyIds)[0];
        const company = companyMap.get(companyId);
        const oldIco = normalizeIco(company?.ico);
        const isChanged = oldIco !== entry.newIco;

        return {
          ...entry,
          companyId,
          companyName: company?.name || '',
          oldIco,
          status: isChanged ? 'changed' : 'same',
          statusLabel: isChanged ? 'Změna' : 'Beze změny',
          reason: '',
        };
      });

      const changed = finalRows.filter((r) => r.status === 'changed').length;
      const same = finalRows.filter((r) => r.status === 'same').length;
      const notFound = finalRows.filter((r) => r.status === 'not-found').length;
      const ambiguous = finalRows.filter((r) => r.status === 'ambiguous').length;
      const invalid = finalRows.filter((r) => r.status === 'invalid').length;

      setPreviewRows(finalRows);
      setStats({
        totalRows: finalRows.length,
        changed,
        same,
        notFound,
        ambiguous,
        invalid,
      });

      setProgress(100);
      setMessage(`Náhled připraven. Změn k přepisu: ${changed}. Prohledáno lokálně ${companies.length} přednačtených firem.`);
    } catch (error) {
      console.error('❌ Chyba při zpracování XLSX:', error);
      setMessage(`❌ Chyba: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const applyChanges = async () => {
    if (changedRows.length === 0) return;

    setIsApplying(true);
    setMessage('Provádím přepis IČO...');
    setProgress(0);

    try {
      const dedupMap = new Map();
      changedRows.forEach((row) => {
        if (!row.companyId || !row.newIco) return;
        dedupMap.set(row.companyId, row.newIco);
      });

      const updates = Array.from(dedupMap.entries()).map(([companyId, ico]) => ({ companyId, ico }));
      const response = await fetch(`${API_URL}/companies/bulk-ico-update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          ...(currentUserId ? { 'X-User-Id': String(currentUserId) } : {}),
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Chyba při ukládání: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      setResult(data);
      setProgress(100);
      setMessage(`✅ Hotovo. Přepsáno ${data.updatedCount || 0} firem.`);
      if (onUpdated) onUpdated();
    } catch (error) {
      console.error('❌ Chyba při přepisu IČO:', error);
      setMessage(`❌ Chyba: ${error.message}`);
    } finally {
      setIsApplying(false);
    }
  };

  const statusClass = (status) => {
    if (status === 'changed') return 'text-orange-600 dark:text-orange-400';
    if (status === 'same') return 'text-gray-500 dark:text-gray-400';
    if (status === 'not-found' || status === 'ambiguous' || status === 'invalid') return 'text-red-600 dark:text-red-400';
    return 'text-gray-500';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} rounded-lg shadow-xl max-w-7xl w-full max-h-[92vh] overflow-y-auto p-6`}>
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-2xl font-bold">🛠️ Oprava IČ dle MSISDN</h2>
          <button
            onClick={onClose}
            className={`${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
            disabled={isProcessing || isApplying}
          >
            ✕
          </button>
        </div>

        <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded p-4 mb-5`}>
          <h3 className="font-semibold mb-2">📋 Požadovaný formát Excelu (.xlsx):</h3>
          <ul className="text-sm space-y-1">
            <li><strong>Sloupec A:</strong> Telefon / MSISDN</li>
            <li><strong>Sloupec B:</strong> Správné IČO</li>
          </ul>
          <p className="text-xs mt-2 opacity-75">Telefon může být s předvolbou, bez předvolby, s mezerami i pomlčkami.</p>
        </div>

        <div className="mb-5">
          <label className="block text-sm font-medium mb-2">Vyberte Excel soubor:</label>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className={`w-full p-2 border rounded-lg ${
              isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
            }`}
            disabled={isProcessing || isApplying}
          />
        </div>

        {(isProcessing || isApplying) && (
          <div className="mb-5">
            <div className="flex justify-between text-sm mb-2">
              <span>{message || 'Zpracovávám...'}</span>
              <span>{progress}%</span>
            </div>
            <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
              <div className="h-2 bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {message && !(isProcessing || isApplying) && (
          <p className="text-sm mb-4">{message}</p>
        )}

        {stats && (
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'} rounded p-4 mb-5`}>
            <h3 className="font-semibold text-blue-600 mb-2">👀 Souhrn náhledu</h3>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
              <div><strong>Řádky:</strong> {stats.totalRows}</div>
              <div><strong>Změny:</strong> {stats.changed}</div>
              <div><strong>Beze změny:</strong> {stats.same}</div>
              <div><strong>Nenalezeno:</strong> {stats.notFound}</div>
              <div><strong>Více firem:</strong> {stats.ambiguous}</div>
              <div><strong>Neplatné:</strong> {stats.invalid}</div>
            </div>
          </div>
        )}

        {previewRows.length > 0 && (
          <div className="mb-5 max-h-[50vh] overflow-auto border border-gray-300 dark:border-gray-600 rounded-lg">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Řádek</th>
                  <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Telefon (A)</th>
                  <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Název firmy</th>
                  <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">IČO původní</th>
                  <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">IČO nové (B)</th>
                  <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Stav</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row) => (
                  <tr key={`${row.rowNumber}-${row.phoneInput}-${row.newIcoInput}`} className={isDarkMode ? 'bg-gray-800' : 'bg-white'}>
                    <td className="border border-gray-300 dark:border-gray-600 p-2">{row.rowNumber}</td>
                    <td className="border border-gray-300 dark:border-gray-600 p-2 font-mono">{row.phoneInput || '—'}</td>
                    <td className="border border-gray-300 dark:border-gray-600 p-2">{row.companyName || '—'}</td>
                    <td className="border border-gray-300 dark:border-gray-600 p-2 font-mono">{row.oldIco || '—'}</td>
                    <td className="border border-gray-300 dark:border-gray-600 p-2 font-mono">{row.newIco || '—'}</td>
                    <td className={`border border-gray-300 dark:border-gray-600 p-2 ${statusClass(row.status)}`}>
                      {row.statusLabel}
                      {row.reason ? ` (${row.reason})` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {result && (
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-green-50'} rounded p-4 mb-5`}>
            <h3 className="font-semibold text-green-600 mb-2">✅ Výsledek přepisu</h3>
            <div className="text-sm space-y-1">
              <p><strong>Přepsáno:</strong> {result.updatedCount || 0}</p>
              <p><strong>Nalezeno:</strong> {result.matchedCount || 0}</p>
              <p><strong>Nenalezeno:</strong> {result.notFoundCount || 0}</p>
              <p><strong>Požadováno:</strong> {result.totalRequested || 0}</p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg ${
              isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
            disabled={isProcessing || isApplying}
          >
            Zavřít
          </button>
          <button
            onClick={buildPreview}
            disabled={!file || isProcessing || isApplying}
            className={`px-4 py-2 rounded-lg font-medium ${
              !file || isProcessing || isApplying
                ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            👀 Zobrazit změny
          </button>
          <button
            onClick={applyChanges}
            disabled={changedRows.length === 0 || isProcessing || isApplying}
            className={`px-4 py-2 rounded-lg font-medium ${
              changedRows.length === 0 || isProcessing || isApplying
                ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {isApplying ? 'Přepisuji...' : `✅ Potvrdit přepis (${changedRows.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
